// app-ui/src/components/canvas/FilteredRequirementNode.tsx
import React, { memo, useState, useEffect, useRef } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FilteredRequirementNodeData } from '@/shared/types/canvas';
import { getLatestBuild, getBuildHistory, BuildDetail } from '@/shared/lib/buildStatusTracker';

// --- Prop Types for Callbacks ---
interface FilteredRequirementNodeActions {
    onContextMenu?: (event: React.MouseEvent, props: any) => void;
    onGenerateChildReq?: (nodeId: string, rowIndex: number) => void;
}

// Note: DEFAULT_COLUMNS removed as we're focusing on user flows only
// Non-user-flow collections show a simple placeholder

// Check if this is a user flow collection (root level)
const isUserFlowCollection = (nodeId: string, title: string): boolean => {
    return nodeId.startsWith('flow_genChildReq_') || title.toLowerCase().includes('user flow');
};

const FilteredRequirementNode: React.FC<NodeProps<FilteredRequirementNodeData & { actions: FilteredRequirementNodeActions; projectId?: string | number }>> = ({ data, id: nodeId, selected }) => {
    const { title, rows, actions, isTestCase = false, connectedRowUiids = [], isLlr = false, projectId } = data;
    
    // Check if this is a user flow collection - if so, render as mind map
    const isUserFlow = isUserFlowCollection(nodeId, title);
    
    // Check if this node contains Low Level Requirements
    const isLowLevelRequirementNode = isLlr || nodeId.startsWith('lowlevelrequirement_');
    
    // State for row selection
    const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
    // State for tracking double-clicked row to show Generate Child Req button
    const [doubleClickedRowIndex, setDoubleClickedRowIndex] = useState<number | null>(null);
    const buttonOverlayRef = useRef<HTMLDivElement>(null);
    // State for Build Status dropdown
    const [openBuildStatusRowIndex, setOpenBuildStatusRowIndex] = useState<number | null>(null);
    const buildStatusDropdownRef = useRef<HTMLDivElement>(null);
    // State to force re-render when localStorage changes
    const [, setRefreshTrigger] = useState(0);

    // Listen for build status changes to trigger re-render
    useEffect(() => {
        if (!isLowLevelRequirementNode) return;

        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'trackBuildStatus') {
                setRefreshTrigger(prev => prev + 1);
            }
        };
        
        const handleCustomStorageChange = () => {
            setRefreshTrigger(prev => prev + 1);
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('buildStatusChanged', handleCustomStorageChange);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('buildStatusChanged', handleCustomStorageChange);
        };
    }, [isLowLevelRequirementNode]);

    // Close button overlay when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (buttonOverlayRef.current && !buttonOverlayRef.current.contains(event.target as Node)) {
                setDoubleClickedRowIndex(null);
            }
            if (buildStatusDropdownRef.current && !buildStatusDropdownRef.current.contains(event.target as Node)) {
                setOpenBuildStatusRowIndex(null);
            }
        };

        if (doubleClickedRowIndex !== null || openBuildStatusRowIndex !== null) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [doubleClickedRowIndex, openBuildStatusRowIndex]);

    // --- Event Handlers ---
    const handleRowClick = (rowIndex: number) => {
        setSelectedRowIndex(rowIndex);
    };

    const handleRowDoubleClick = (rowIndex: number, event: React.MouseEvent) => {
        event.stopPropagation();
        setDoubleClickedRowIndex(rowIndex);
    };

    const handleGenerateChildReq = (rowIndex: number) => {
        if (actions.onGenerateChildReq) {
            actions.onGenerateChildReq(nodeId, rowIndex);
        }
        setDoubleClickedRowIndex(null);
    };

    const displayContextMenu = (event: React.MouseEvent, type: 'header' | 'row' | 'background', rowIndex?: number, colKey?: string) => {
        // If right-clicking on a row, also select it
        if (type === 'row' && rowIndex !== undefined) {
            setSelectedRowIndex(rowIndex);
        }
        
        // Get UIID information for rows
        let uiid: string | undefined;
        let parentUiid: string | undefined;
        
        if (type === 'row' && rowIndex !== undefined && rows && rows[rowIndex]) {
            // Extract the UIID from the row data
            uiid = rows[rowIndex].uiid || rows[rowIndex].id;
            
            // Try to get parent UIID if available in originalData
            if (rows[rowIndex].originalData) {
                const originalData = rows[rowIndex].originalData;
                if (originalData.parent_uiid) {
                    parentUiid = originalData.parent_uiid;
                }
            }
        }
        
        event.preventDefault();
        event.stopPropagation();
        // Parent (CanvasViewer) opens the branch-focus menu (portal) so it is not broken by React Flow / contexify.
        actions.onContextMenu?.(event, {
            nodeId,
            type,
            rowIndex,
            colKey,
            uiid,
            parentUiid,
            isTestCase
        });
    };

    // Sort rows by order (sno) for sequential display
    const sortedRows = [...rows].sort((a, b) => (a.sno || 0) - (b.sno || 0));

    const handleColor = '#8B5CF6';

    const renderCommonHandles = () => (
        <>
            {/* Parent connection */}
            <Handle
                type="target"
                position={Position.Top}
                id={`${nodeId}-parent`}
                style={{
                    top: -6,
                    left: '50%',
                    width: 12,
                    height: 12,
                    background: handleColor,
                    border: `2px solid ${handleColor}`,
                    borderRadius: '50%',
                    opacity: 0.8,
                    zIndex: 9999,
                    position: 'absolute',
                    transform: 'translate(-50%, -50%)',
                }}
            />

            {/* Child connection */}
            <Handle
                type="source"
                position={Position.Bottom}
                id={`${nodeId}-child`}
                style={{
                    bottom: -6,
                    left: '50%',
                    width: 12,
                    height: 12,
                    background: handleColor,
                    border: `2px solid ${handleColor}`,
                    borderRadius: '50%',
                    opacity: 0.8,
                    zIndex: 9999,
                    position: 'absolute',
                    transform: 'translate(-50%, 50%)',
                }}
            />

            {/* Sibling handles */}
            <Handle
                type="target"
                position={Position.Left}
                id={`${nodeId}-sibling-in`}
                style={{
                    top: '50%',
                    left: -6,
                    width: 12,
                    height: 12,
                    background: handleColor,
                    border: `2px solid ${handleColor}`,
                    borderRadius: '50%',
                    opacity: 0.8,
                    zIndex: 9999,
                    position: 'absolute',
                    transform: 'translateY(-50%)',
                }}
            />

            <Handle
                type="source"
                position={Position.Right}
                id={`${nodeId}-sibling-out`}
                style={{
                    top: '50%',
                    right: -6,
                    width: 12,
                    height: 12,
                    background: handleColor,
                    border: `2px solid ${handleColor}`,
                    borderRadius: '50%',
                    opacity: 0.8,
                    zIndex: 9999,
                    position: 'absolute',
                    transform: 'translateY(-50%)',
                }}
            />
        </>
    );

    // --- Rendering ---
    // For user flows, render as mind map
    if (isUserFlow) {
        return (
            <div
                className={`
                    min-w-[300px] max-w-[800px]
                    border-2 rounded-xl shadow-lg bg-white
                    ${selected ? 'border-purple-500' : 'border-gray-300'}
                    overflow-visible
                    relative
                    p-4
                `}
                onContextMenu={(e) => displayContextMenu(e, 'background')}
            >
                {/* Title */}
                <div className="text-center font-bold text-lg mb-4 text-gray-800 pb-2 border-b border-gray-200">
                    {title}
                </div>

                {/* Mind Map Container */}
                <div className="flex flex-col gap-4 relative">
                    {sortedRows.map((row, rowIndex) => {
                        const isLast = rowIndex === sortedRows.length - 1;
                        const rowName = row.name || `Flow ${row.sno || rowIndex + 1}`;
                        const rowDesc = row.desc || '';
                        const rowUiid = row.uiid || row.id;

                        return (
                            <div key={`mindmap-row-${row.id}-${rowIndex}`} className="relative">
                                {/* Connection Line (arrow pointing to next node) */}
                                {!isLast && (
                                    <div className="absolute left-1/2 top-full w-0.5 h-4 bg-purple-400 transform -translate-x-1/2 z-0">
                                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-purple-400"></div>
                                    </div>
                                )}

                                {/* Flow Node Card */}
                                <div
                                    className={`
                                        bg-gradient-to-br from-purple-50 to-blue-50
                                        border-2 rounded-lg p-4 shadow-md
                                        hover:shadow-lg transition-all duration-200
                                        ${selectedRowIndex === rowIndex ? 'border-purple-500 ring-2 ring-purple-200' : 'border-purple-200'}
                                        cursor-pointer relative z-10
                                    `}
                                    onClick={() => handleRowClick(rowIndex)}
                                    onDoubleClick={(e) => handleRowDoubleClick(rowIndex, e)}
                                    onContextMenu={(e) => displayContextMenu(e, 'row', rowIndex)}
                                    title={`UIID: ${rowUiid}`}
                                >
                                    {/* Order Badge */}
                                    <div className="absolute -top-2 -left-2 w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md">
                                        {row.sno || rowIndex + 1}
                                    </div>
                                    
                                    {/* Status Indicator in top-right corner - Only for Low Level Requirements */}
                                    {isLowLevelRequirementNode && projectId && (() => {
                                        const trackedLatestBuild = getLatestBuild(projectId, rowUiid);
                                        const status = trackedLatestBuild?.build_status || row.originalData?.build_status || 'Not Started';
                                        
                                        const getStatusColor = () => {
                                            if (status === 'Completed') return 'bg-green-500 opacity-100';
                                            if (status === 'In Progress') return 'bg-blue-500 opacity-100';
                                            if (status === 'Error') return 'bg-red-500 opacity-100';
                                            return 'bg-gray-500 opacity-100';
                                        };
                                        
                                        // 2-letter badges (small circle): NS Not started, IP In progress, ER Error, CP Complete
                                        const getStatusLabel = () => {
                                            if (status === 'Completed') return 'CP';
                                            if (status === 'In Progress') return 'IP';
                                            if (status === 'Error') return 'ER';
                                            return 'NS';
                                        };

                                        return (
                                            <div 
                                                className={`absolute -top-2 -right-2 w-8 h-8 ${getStatusColor()} rounded-full shadow-lg flex items-center justify-center text-white text-xs font-bold`} 
                                                title={status}
                                            >
                                                {getStatusLabel()}
                                            </div>
                                        );
                                    })()}

                                    {/* Flow Name */}
                                    <div className="font-semibold text-gray-800 text-base mb-2 pr-6 line-clamp-2">
                                        {rowName}
                                    </div>

                                    {/* Flow Description */}
                                    {rowDesc && (
                                        <div className="text-sm text-gray-600 mt-2 line-clamp-2">
                                            {rowDesc}
                                        </div>
                                    )}

                                    {/* UUID, Version, Updated, and conditional fields for LLRs */}
                                    <div className="mt-2">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-gray-700 font-semibold text-sm">UIID:</span>
                                            <span className="font-mono text-gray-900 font-medium text-sm">{rowUiid}</span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-gray-700 font-semibold">Version:</span>
                                                <span className="text-gray-900 font-medium">{row.originalData?.version || '1.0'}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-gray-700 font-semibold">Updated:</span>
                                                <span className="text-gray-900 font-medium">{(() => {
                                                    const updatedAt = row.originalData?.updated_at || row.originalData?.created_at;
                                                    if (!updatedAt) return 'N/A';
                                                    try {
                                                        const date = new Date(updatedAt);
                                                        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                                                    } catch {
                                                        return 'N/A';
                                                    }
                                                })()}</span>
                                            </div>
                                            {/* Build Status Button - Only for Low Level Requirements - Moved to right */}
                                            {isLowLevelRequirementNode && projectId && (() => {
                                                // Get build status from localStorage tracking
                                                const trackedLatestBuild = getLatestBuild(projectId, rowUiid);
                                                const trackedHistory = getBuildHistory(projectId, rowUiid);
                                                
                                                // If no tracked entries, show "Not Started" and empty dropdown
                                                const hasTrackedEntries = trackedLatestBuild !== null;
                                                
                                                // Use tracked data if available, otherwise fall back to originalData or "Not Started"
                                                const buildStatus = hasTrackedEntries 
                                                    ? (trackedLatestBuild?.build_status || 'Not Started')
                                                    : (row.originalData?.build_status || 'Not Started');
                                                const buildInitiatedAt = hasTrackedEntries
                                                    ? (trackedLatestBuild?.build_initiated_at || null)
                                                    : (row.originalData?.build_initiated_at || row.originalData?.launched_at || row.originalData?.launch_date || null);
                                                const gitCommit = hasTrackedEntries
                                                    ? (trackedLatestBuild?.git_commit || null)
                                                    : (row.originalData?.git_commit || null);
                                                const buildHistory = trackedHistory.length > 0 ? trackedHistory : (row.originalData?.build_history || []);
                                                
                                                // Determine button color based on status
                                                const getStatusButtonColor = () => {
                                                    if (buildStatus === 'Completed') return 'bg-green-600 hover:bg-green-700 text-white';
                                                    if (buildStatus === 'In Progress') return 'bg-blue-600 hover:bg-blue-700 text-white';
                                                    if (buildStatus === 'Error') return 'bg-red-600 hover:bg-red-700 text-white';
                                                    return 'bg-gray-600 hover:bg-gray-700 text-white'; // Not Started
                                                };
                                                
                                                const formatDate = (dateString: string | null | undefined): string => {
                                                    if (!dateString) return 'N/A';
                                                    try {
                                                        const date = new Date(dateString);
                                                        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                                                    } catch {
                                                        return 'N/A';
                                                    }
                                                };
                                                
                                                // Sort history by build_initiated_at descending (for tracked builds) or date (for originalData)
                                                const sortedHistory = trackedHistory.length > 0 
                                                    ? trackedHistory
                                                    : [...buildHistory].sort((a: any, b: any) => {
                                                        const dateA = a.timestamp || a.date || a.created_at || a.build_initiated_at || 0;
                                                        const dateB = b.timestamp || b.date || b.created_at || b.build_initiated_at || 0;
                                                        return new Date(dateB).getTime() - new Date(dateA).getTime();
                                                    });
                                                
                                                return (
                                                    <div className="relative ml-auto" ref={rowIndex === openBuildStatusRowIndex ? buildStatusDropdownRef : null}>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenBuildStatusRowIndex(openBuildStatusRowIndex === rowIndex ? null : rowIndex);
                                                            }}
                                                            className={`
                                                                px-3 py-1.5 rounded-lg text-xs font-semibold transition-all
                                                                flex items-center gap-2
                                                                ${getStatusButtonColor()}
                                                            `}
                                                        >
                                                            <span>{buildStatus}</span>
                                                            <svg 
                                                                className={`w-3 h-3 transition-transform ${openBuildStatusRowIndex === rowIndex ? 'rotate-180' : ''}`}
                                                                fill="none" 
                                                                stroke="currentColor" 
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </button>
                                                        
                                                        {openBuildStatusRowIndex === rowIndex && (
                                                            <div className="absolute right-0 mt-1 w-64 bg-white border-2 border-gray-300 rounded-lg shadow-xl z-50 p-3">
                                                                {hasTrackedEntries || buildHistory.length > 0 ? (
                                                                    <div className="space-y-2">
                                                                        <div className="border-b border-gray-200 pb-2">
                                                                            <div className="text-xs font-semibold text-gray-700 mb-1">Git Commit</div>
                                                                            <div className={`font-mono text-sm ${gitCommit ? 'text-green-600' : 'text-gray-600'}`}>
                                                                                {gitCommit ? gitCommit.substring(0, 7) : 'N/A'}
                                                                            </div>
                                                                        </div>
                                                                        <div className="border-b border-gray-200 pb-2">
                                                                            <div className="text-xs font-semibold text-gray-700 mb-1">Build Initiated</div>
                                                                            <div className="text-sm text-gray-900">
                                                                                {formatDate(buildInitiatedAt)}
                                                                            </div>
                                                                        </div>
                                                                        <div className="border-b border-gray-200 pb-2">
                                                                            <div className="text-xs font-semibold text-gray-700 mb-1">Build Status</div>
                                                                            <div className={`text-sm font-semibold ${
                                                                                buildStatus === 'Completed' ? 'text-green-700' :
                                                                                buildStatus === 'In Progress' ? 'text-blue-700' :
                                                                                buildStatus === 'Error' ? 'text-red-700' :
                                                                                'text-gray-900'
                                                                            }`}>
                                                                                {buildStatus}
                                                                            </div>
                                                                        </div>
                                                                        {sortedHistory.length > 0 && (
                                                                            <div>
                                                                                <div className="text-xs font-semibold text-gray-700 mb-2">History</div>
                                                                                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                                                                    {sortedHistory.map((entry: BuildDetail | any, idx: number) => {
                                                                                        // Handle both BuildDetail format and originalData format
                                                                                        const entryStatus = entry.build_status || entry.status || 'Unknown';
                                                                                        const entryDate = entry.build_initiated_at || entry.timestamp || entry.date || entry.created_at;
                                                                                        const entryCommit = entry.git_commit;
                                                                                        return (
                                                                                            <div key={idx} className="text-xs p-2 bg-gray-50 rounded border border-gray-200">
                                                                                                <div className="flex justify-between items-start mb-1">
                                                                                                    <span className={`font-semibold ${
                                                                                                        entryStatus === 'Completed' ? 'text-green-700' :
                                                                                                        entryStatus === 'In Progress' ? 'text-blue-700' :
                                                                                                        entryStatus === 'Error' ? 'text-red-700' :
                                                                                                        'text-gray-900'
                                                                                                    }`}>
                                                                                                        {entryStatus}
                                                                                                    </span>
                                                                                                    <span className="text-gray-500 text-xs">
                                                                                                        {formatDate(entryDate)}
                                                                                                    </span>
                                                                                                </div>
                                                                                                {entryCommit && (
                                                                                                    <div className="text-xs text-gray-600 font-mono">
                                                                                                        {typeof entryCommit === 'string' ? entryCommit.substring(0, 7) : entryCommit}
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-sm text-gray-500 text-center py-4">
                                                                        No Build Entries
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    {/* Generate Child Req Button - shown on double-click */}
                                    {doubleClickedRowIndex === rowIndex && (
                                        <div 
                                            ref={buttonOverlayRef}
                                            className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center z-20"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleGenerateChildReq(rowIndex);
                                                }}
                                                className="bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg font-semibold shadow-lg transition-all duration-200 transform hover:scale-105"
                                            >
                                                Generate Child Req
                                            </button>
                                        </div>
                                    )}

                                    {/* Connection Handle */}
                                    {connectedRowUiids.includes(rowUiid) && (
                                        <Handle
                                            type="source"
                                            position={Position.Right}
                                            id={`row-handle-${rowUiid}`}
                                            style={{
                                                right: -6,
                                                top: '50%',
                                                width: 12,
                                                height: 12,
                                                background: '#8B5CF6',
                                                border: '2px solid #8B5CF6',
                                                borderRadius: '50%',
                                                opacity: 0.8,
                                                zIndex: 9999,
                                                position: 'absolute',
                                                transform: 'translateY(-50%)',
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {renderCommonHandles()}
            </div>
        );
    }

    // For non-user-flow collections, show simple placeholder (focusing on user flows only)
    return (
        <div
            className={`
                min-w-[200px] max-w-[400px]
                border-2 rounded-xl shadow-lg bg-gray-50
                ${selected ? 'border-purple-500' : 'border-gray-300'}
                p-4
                relative
            `}
            onContextMenu={(e) => displayContextMenu(e, 'background')}
        >
            <div className="text-center text-gray-600 text-sm">
                <div className="font-semibold mb-2">{title}</div>
                <div className="text-xs">(Other collection types - simplified view)</div>
            </div>
            {renderCommonHandles()}
        </div>
    );
};

// Use memo to prevent unnecessary re-renders
export default memo(FilteredRequirementNode);

